import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Search, X, Plus, Edit3, Check } from "lucide-react";
import { CryptoIcon } from "../icons/CryptoIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSETS = ["TON", "TRX", "BNB"];

const AssetsTab = ({ token }) => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [creditModal, setCreditModal] = useState(null);
  const [addressModal, setAddressModal] = useState(null);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/mnro/assets/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(res.data);
    } catch (e) {
      toast.error("Ошибка загрузки активов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [token]); // eslint-disable-line

  const filtered = overview.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.client_id?.toLowerCase().includes(q) ||
      u.id?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-chivo font-bold text-xl text-white flex items-center gap-2">
            <Coins size={20} className="text-[#EDEDED]" />
            Активы клиентов
          </h2>
          <p className="text-[#A1A1AA] text-sm mt-1">Начисление и адреса пополнения для TON / TRX / BNB</p>
        </div>
        <div className="text-[#A1A1AA] text-sm">
          Всего: <span className="text-white font-medium">{overview.length}</span>
        </div>
      </div>

      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по ID, имени или email..."
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-10 py-2.5 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              data-testid="assets-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-8 text-center text-[#A1A1AA]">Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[#A1A1AA]">Ничего не найдено</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Клиент</th>
                  {ASSETS.map((a) => (
                    <th key={a} className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">
                      {a}
                    </th>
                  ))}
                  <th className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-[#1F1F1F] hover:bg-[#1A1A1A]/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-white/20 text-[#EDEDED] flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-white text-sm font-medium truncate">{u.name}</div>
                          <div className="text-[#71717A] text-xs truncate">{u.email}</div>
                          {u.client_id && (
                            <span className="text-[10px] text-[#EDEDED]">#{u.client_id}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    {ASSETS.map((a) => (
                      <td key={a} className="px-4 py-3 text-right">
                        <div className="font-mono text-white text-sm">
                          {Number(u.assets?.[a] || 0).toFixed(4)}
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setCreditModal({ user: u, asset: "TON", amount: "", mode: "delta" })}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#FF8A1E] hover:bg-[#E67A00] text-white text-xs font-semibold rounded transition-colors"
                          data-testid={`credit-btn-${u.id}`}
                        >
                          <Plus size={14} /> Изменить
                        </button>
                        <button
                          onClick={() => setAddressModal({ user: u, addresses: { ...(u.asset_addresses || {}) } })}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] hover:bg-[#1F1F1F] text-[#A1A1AA] hover:text-white text-xs rounded border border-[#1F1F1F] transition-colors"
                          title="Адреса для пополнения"
                        >
                          <Edit3 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Credit modal */}
      {creditModal && (
        <CreditModal
          state={creditModal}
          setState={setCreditModal}
          token={token}
          onSuccess={(freshAssets) => {
            // Optimistic update — patch this user's row in-place, no full refetch needed
            if (freshAssets) {
              setOverview((prev) =>
                prev.map((u) =>
                  u.id === creditModal.user.id ? { ...u, assets: { ...u.assets, ...freshAssets } } : u
                )
              );
            }
            setCreditModal(null);
          }}
        />
      )}

      {/* Address modal */}
      {addressModal && (
        <AddressModal
          state={addressModal}
          setState={setAddressModal}
          token={token}
          onSuccess={(freshAddresses) => {
            if (freshAddresses) {
              setOverview((prev) =>
                prev.map((u) =>
                  u.id === addressModal.user.id ? { ...u, asset_addresses: freshAddresses } : u
                )
              );
            }
            setAddressModal(null);
          }}
        />
      )}
    </div>
  );
};

const CreditModal = ({ state, setState, token, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const mode = state.mode || "delta";
  const currentBalance = Number(state.user.assets?.[state.asset] || 0);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(state.amount);
    if (isNaN(amount)) {
      toast.error("Введите сумму");
      return;
    }
    if (mode === "delta" && amount === 0) {
      toast.error("Сумма должна быть не равна 0");
      return;
    }
    if (mode === "set" && amount < 0) {
      toast.error("Баланс не может быть отрицательным");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/mnro/users/${state.user.id}/assets/credit`,
        { asset: state.asset, amount, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const msg =
        mode === "set"
          ? `Установлено: ${amount} ${state.asset} (было ${res.data.previous.toFixed(4)})`
          : `${amount > 0 ? "Начислено" : "Списано"} ${Math.abs(amount)} ${state.asset}`;
      toast.success(msg);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  // Preview value
  let previewValue = currentBalance;
  const parsed = parseFloat(state.amount);
  if (!isNaN(parsed)) {
    previewValue = mode === "set" ? parsed : currentBalance + parsed;
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/70-sm flex items-center justify-center p-4" onClick={() => setState(null)}>
      <div
        className="w-full max-w-md bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h3 className="font-bold text-white">Изменить баланс актива</h3>
          <button onClick={() => setState(null)} className="text-[#A1A1AA] hover:text-white">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <div className="text-[#A1A1AA] text-sm mb-2">Клиент</div>
            <div className="bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white text-sm">
              {state.user.name} — {state.user.email}
            </div>
          </div>

          <div>
            <div className="text-[#A1A1AA] text-sm mb-2">Актив</div>
            <div className="grid grid-cols-3 gap-2">
              {ASSETS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setState({ ...state, asset: a, amount: "" })}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded border transition-colors ${
                    state.asset === a
                      ? "border-white bg-white/10 text-white"
                      : "border-[#1F1F1F] bg-[#000000] text-[#A1A1AA] hover:border-[#2E2E2E]/40"
                  }`}
                >
                  <CryptoIcon code={a} size={24} />
                  <div className="text-xs font-semibold">{a}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Mode toggle */}
          <div>
            <div className="text-[#A1A1AA] text-sm mb-2">Режим</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setState({ ...state, mode: "delta", amount: "" })}
                className={`px-3 py-2 rounded border text-xs font-semibold transition-colors ${
                  mode === "delta"
                    ? "border-white bg-white/10 text-white"
                    : "border-[#1F1F1F] bg-[#000000] text-[#A1A1AA] hover:border-[#2E2E2E]/40"
                }`}
                data-testid="mode-delta-btn"
              >
                Начислить / списать
              </button>
              <button
                type="button"
                onClick={() => setState({ ...state, mode: "set", amount: "" })}
                className={`px-3 py-2 rounded border text-xs font-semibold transition-colors ${
                  mode === "set"
                    ? "border-white bg-white/10 text-white"
                    : "border-[#1F1F1F] bg-[#000000] text-[#A1A1AA] hover:border-[#2E2E2E]/40"
                }`}
                data-testid="mode-set-btn"
              >
                Установить точно
              </button>
            </div>
            <p className="text-[#71717A] text-[11px] mt-1.5">
              {mode === "delta"
                ? "Добавить к балансу (или вычесть, если отрицательное)"
                : "Заменить текущий баланс на введённое значение"}
            </p>
          </div>

          <div>
            <div className="text-[#A1A1AA] text-sm mb-2">
              {mode === "set" ? "Новый баланс" : "Сумма (+/-)"}
            </div>
            <input
              type="number"
              step="any"
              value={state.amount}
              onChange={(e) => setState({ ...state, amount: e.target.value })}
              placeholder={mode === "set" ? "Точное значение" : "+50 или -50"}
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white focus:border-white focus:outline-none font-mono"
              data-testid="credit-amount-input"
              autoFocus
            />
            <div className="text-[#71717A] text-xs mt-2 space-y-0.5">
              <div>
                Сейчас:{" "}
                <span className="text-white font-mono">{currentBalance.toFixed(4)}</span> {state.asset}
              </div>
              {!isNaN(parsed) && (
                <div>
                  Станет:{" "}
                  <span className="text-[#EDEDED] font-mono">{previewValue.toFixed(4)}</span> {state.asset}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 bg-[#FF8A1E] hover:bg-[#E67A00] disabled:opacity-50 text-white font-semibold rounded transition-colors"
            data-testid="credit-submit-btn"
          >
            {saving ? "Сохранение..." : mode === "set" ? "Установить" : "Применить"}
          </button>
        </form>
      </div>
    </div>
  );
};

const AddressModal = ({ state, setState, token, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/mnro/users/${state.user.id}/assets/addresses`,
        state.addresses,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Адреса обновлены");
      onSuccess(res.data.asset_addresses);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70-sm flex items-center justify-center p-4" onClick={() => setState(null)}>
      <div
        className="w-full max-w-lg bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h3 className="font-bold text-white">Адреса пополнения активов</h3>
          <button onClick={() => setState(null)} className="text-[#A1A1AA] hover:text-white">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-[#71717A] text-xs mb-2">
            Установите персональные адреса для каждого актива. Оставьте пустым для значения по умолчанию.
          </div>
          {ASSETS.map((a) => (
            <div key={a}>
              <div className="text-[#A1A1AA] text-sm mb-2 flex items-center gap-2">
                <CryptoIcon code={a} size={20} />
                <span>Адрес для {a}</span>
              </div>
              <input
                type="text"
                value={state.addresses[a] || ""}
                onChange={(e) =>
                  setState({ ...state, addresses: { ...state.addresses, [a]: e.target.value } })
                }
                placeholder={`Введите ${a} адрес...`}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white font-mono text-xs focus:border-white focus:outline-none"
                data-testid={`address-input-${a}`}
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 bg-[#FF8A1E] hover:bg-[#E67A00] disabled:opacity-50 text-white font-semibold rounded transition-colors flex items-center justify-center gap-2"
          >
            <Check size={16} /> {saving ? "Сохранение..." : "Сохранить"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AssetsTab;
